import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Briefcase, Mail, MessageCircle, Edit, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["Sonoma", "Napa", "Marin", "Lake", "Mendocino"];

export default function ProfilePage({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isConnected, setIsConnected] = useState(false);

  const isOwnProfile = user.id === id;

  useEffect(() => {
    fetchProfile();
    if (!isOwnProfile) {
      checkConnection();
    }
  }, [id]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error("Failed to fetch profile", error);
    } finally {
      setLoading(false);
    }
  };

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const connected = response.data.some(
        (conn) => conn.connected_user_id === id || conn.user_id === id
      );
      setIsConnected(connected);
    } catch (error) {
      console.error("Failed to check connection", error);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/users/profile`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
      setEditDialogOpen(false);
      fetchProfile();
      
      const updatedUser = { ...user, ...editData };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/connections`, { connected_user_id: id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Connection added");
      setIsConnected(true);
    } catch (error) {
      toast.error("Failed to connect");
    }
  };

  const handleMessage = () => {
    navigate(`/messages?userId=${id}`);
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!profile) {
    return <div className="text-center py-12">Profile not found</div>;
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/community")}
        className="mb-4"
        data-testid="back-to-community-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Community
      </Button>

      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-teal-50">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            <Avatar className="h-24 w-24" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}>
              <AvatarFallback className="text-white text-3xl font-bold">
                {getInitials(profile.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-3xl mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {profile.name}
              </CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                {profile.trade_focus && (
                  <Badge variant="outline" className="text-base">{profile.trade_focus}</Badge>
                )}
                <Badge className={profile.role === "graduate" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}>
                  {profile.role === "graduate" ? "Graduate" : "Student"}
                </Badge>
                {profile.grad_year && (
                  <Badge variant="outline">Class of {profile.grad_year}</Badge>
                )}
              </div>
            </div>
            {isOwnProfile ? (
              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="edit-profile-button" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Profile
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Profile</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={editData.name || ""}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        data-testid="edit-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Trade Focus</Label>
                      <Input
                        value={editData.trade_focus || ""}
                        onChange={(e) => setEditData({ ...editData, trade_focus: e.target.value })}
                        data-testid="edit-trade-focus-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>County</Label>
                      <Select
                        value={editData.county || ""}
                        onValueChange={(value) => setEditData({ ...editData, county: value })}
                      >
                        <SelectTrigger data-testid="edit-county-select">
                          <SelectValue placeholder="Select county" />
                        </SelectTrigger>
                        <SelectContent>
                          {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {profile.role === "graduate" && (
                      <div className="space-y-2">
                        <Label>Graduation Year</Label>
                        <Input
                          type="number"
                          value={editData.grad_year || ""}
                          onChange={(e) => setEditData({ ...editData, grad_year: parseInt(e.target.value) })}
                          data-testid="edit-grad-year-input"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={editData.bio || ""}
                        onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                        rows={4}
                        data-testid="edit-bio-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Preference</Label>
                      <Input
                        value={editData.contact_preference || ""}
                        onChange={(e) => setEditData({ ...editData, contact_preference: e.target.value })}
                        placeholder="e.g., Open to networking"
                        data-testid="edit-contact-preference-input"
                      />
                    </div>
                    <Button
                      onClick={handleUpdateProfile}
                      className="w-full"
                      data-testid="save-profile-button"
                      style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <div className="flex gap-2">
                {!isConnected && (
                  <Button
                    onClick={handleConnect}
                    variant="outline"
                    data-testid="connect-button"
                  >
                    Connect
                  </Button>
                )}
                <Button
                  onClick={handleMessage}
                  data-testid="message-button"
                  style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Message
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`mailto:compliance@ngta.com?subject=Report User: ${profile.name}&body=User ID: ${profile.id}%0D%0A%0D%0APlease describe the issue:`, '_blank')}
                  className="text-red-600 hover:text-red-700 border-red-300"
                  data-testid="report-profile-button"
                >
                  Report
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
              <div>
                <p className="font-medium text-gray-900">Email</p>
                <p className="text-gray-600">{profile.email}</p>
              </div>
            </div>
            {profile.county && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
                <div>
                  <p className="font-medium text-gray-900">County</p>
                  <p className="text-gray-600">{profile.county}</p>
                </div>
              </div>
            )}
            {profile.trade_focus && (
              <div className="flex items-start gap-3">
                <Briefcase className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
                <div>
                  <p className="font-medium text-gray-900">Trade Focus</p>
                  <p className="text-gray-600">{profile.trade_focus}</p>
                </div>
              </div>
            )}
            {profile.grad_year && (
              <div className="flex items-start gap-3">
                <GraduationCap className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
                <div>
                  <p className="font-medium text-gray-900">Graduation Year</p>
                  <p className="text-gray-600">{profile.grad_year}</p>
                </div>
              </div>
            )}
          </div>

          {profile.bio && (
            <div className="border-t pt-6">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>About</h3>
              <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {profile.contact_preference && (
            <div className="border-t pt-6">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Contact Preference</h3>
              <p className="text-gray-700">{profile.contact_preference}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}